/**
 * The only place the app talks to the outside world.
 *
 * `VITE_QUANTIST_API_BASE` points at the Cloudflare worker in `worker/`, never
 * at quantist_api directly: the API key is a worker secret and must never be
 * bundled into browser source. With no base URL configured — the default for
 * local development — the committed COMPOSITE fixture is served instead, so
 * the workspace runs with no backend at all.
 *
 * Everything is injectable rather than read from globals, so the tests drive
 * it without a network, a DOM or an env file.
 */
import compositeFixture from "../fixtures/composite.fixture.json" with { type: "json" };
import { assertWebChart, type WebChart } from "../domain/chart.ts";
import { resolveCode, DEFAULT_CODE } from "../domain/instruments.ts";

/** The backend's two analysis methods. `broker` is shown as "Whale". */
export type AnalysisMethod = "foreign" | "broker";
export const DEFAULT_METHOD: AnalysisMethod = "broker";

export function isAnalysisMethod(value: unknown): value is AnalysisMethod {
	return value === "foreign" || value === "broker";
}

export interface ChartSource {
	/** Base URL of the read boundary, e.g. https://chart.example.workers.dev/web-api/v1. */
	baseUrl?: string | null;
	/** Which flow to read. Forwarded to the worker, which forwards it to the API. */
	method?: AnalysisMethod;
	fetchImpl?: typeof fetch;
	signal?: AbortSignal;
}

export interface ChartResult {
	chart: WebChart;
	/** Where the payload came from, so the UI can say so instead of implying live data. */
	origin: "api" | "fixture";
}

export class ChartRequestError extends Error {
	constructor(message: string, readonly status?: number) {
		super(message);
		this.name = "ChartRequestError";
	}
}

/** Fixtures exist for exactly one instrument; anything else needs the API. */
const FIXTURES: Readonly<Record<string, unknown>> = { [DEFAULT_CODE]: compositeFixture };

export function fixtureFor(code: string): WebChart | null {
	const fixture = FIXTURES[resolveCode(code)];
	if (!fixture) return null;
	assertWebChart(fixture);
	return fixture;
}

/** Trailing slashes are a configuration typo, not a different endpoint. */
function normalizeBase(value: string | null | undefined): string | null {
	const trimmed = typeof value === "string" ? value.trim().replace(/\/+$/, "") : "";
	return trimmed || null;
}

export function apiBaseUrl(): string | null {
	return normalizeBase(import.meta.env?.VITE_QUANTIST_API_BASE);
}

export async function fetchChart(code: string, source: ChartSource = {}): Promise<ChartResult> {
	const canonical = resolveCode(code);
	const method = source.method ?? DEFAULT_METHOD;
	const baseUrl = source.baseUrl === undefined ? apiBaseUrl() : normalizeBase(source.baseUrl);

	if (!baseUrl) {
		// The bundled fixture is one method's data. Offering it for the other
		// would be inventing a Foreign chart out of Whale numbers.
		const fixture = method === DEFAULT_METHOD ? fixtureFor(canonical) : null;
		if (!fixture) {
			throw new ChartRequestError(
				`No offline fixture for "${canonical}" (${method}) and no API base URL is configured.`,
			);
		}
		return { chart: fixture, origin: "fixture" };
	}

	const request = source.fetchImpl ?? fetch;
	let response: Response;
	try {
		response = await request(`${baseUrl}/chart/${encodeURIComponent(canonical)}?method=${method}`, {
			headers: { accept: "application/json" },
			...(source.signal ? { signal: source.signal } : {}),
		});
	} catch (cause) {
		throw new ChartRequestError(`Chart request for "${canonical}" failed: ${(cause as Error).message}`);
	}

	if (!response.ok) {
		throw new ChartRequestError(`Chart request for "${canonical}" returned ${response.status}.`, response.status);
	}

	const payload = await response.json();
	assertWebChart(payload);
	return { chart: payload, origin: "api" };
}
